import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Radio, CircleOff, Pencil, ChevronDown, ChevronRight, Zap, Star, Bell, Shield, BarChart3, Users, FileText, Settings, Heart, Award, Lightbulb, Package, Sparkles, Clock } from 'lucide-react';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const ROLES = [
  { value: 'system_owner',        label: 'System Owner' },
  { value: 'tenant_system_admin', label: 'Tenant Admin' },
  { value: 'tenant_team_leader',  label: 'Team Leader' },
  { value: 'tenant_team_member',  label: 'Team Member' },
  { value: 'tenant_card_runner',  label: 'Card Runner' },
  { value: 'tenant_sale_clerk',   label: 'Sale Clerk' },
  { value: 'tenant_other',        label: 'View Only' },
  { value: 'owner',               label: 'Horse Owner' },
];

const ICONS = [
  { value: 'Zap', label: 'Zap', Icon: Zap },
  { value: 'Star', label: 'Star', Icon: Star },
  { value: 'Bell', label: 'Bell', Icon: Bell },
  { value: 'Shield', label: 'Shield', Icon: Shield },
  { value: 'BarChart3', label: 'Chart', Icon: BarChart3 },
  { value: 'Users', label: 'Users', Icon: Users },
  { value: 'FileText', label: 'Document', Icon: FileText },
  { value: 'Settings', label: 'Settings', Icon: Settings },
  { value: 'Heart', label: 'Heart', Icon: Heart },
  { value: 'Award', label: 'Award', Icon: Award },
  { value: 'Lightbulb', label: 'Idea', Icon: Lightbulb },
  { value: 'Package', label: 'Package', Icon: Package },
  { value: 'Sparkles', label: 'Sparkles', Icon: Sparkles },
  { value: 'Clock',    label: 'Clock',    Icon: Clock    },
];

const IconComponent = ({ name, className }) => {
  const found = ICONS.find(i => i.value === name);
  if (!found) return <Zap className={className} />;
  const { Icon } = found;
  return <Icon className={className} />;
};

const BLANK_FEATURE = { title: '', description: '', roles: [], icon: 'Zap' };

const FeatureEditor = ({ feature, onChange, onRemove }) => {
  const toggleRole = (role) => {
    const current = feature.roles || [];
    const updated = current.includes(role) ? current.filter(r => r !== role) : [...current, role];
    onChange({ ...feature, roles: updated });
  };

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Feature Title *</Label>
          <Input value={feature.title} onChange={e => onChange({ ...feature, title: e.target.value })}
            placeholder="e.g. Smarter Horse Notes" className="h-8 text-sm" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Icon</Label>
          <Select value={feature.icon || 'Zap'} onValueChange={v => onChange({ ...feature, icon: v })}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ICONS.map(({ value, label, Icon }) => (
                <SelectItem key={value} value={value}>
                  <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5" />{label}</div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Description *</Label>
        <Textarea value={feature.description} onChange={e => onChange({ ...feature, description: e.target.value })}
          placeholder="What changed and why it matters..." rows={2} className="text-sm resize-none" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Show to (empty = all users)</Label>
        <div className="flex flex-wrap gap-1.5">
          {ROLES.map(r => {
            const on = (feature.roles || []).includes(r.value);
            return (
              <button key={r.value} type="button" onClick={() => toggleRole(r.value)}
                className={`text-[11px] px-2.5 py-0.5 rounded-full border transition-colors ${on
                  ? 'bg-primary text-white border-primary'
                  : 'border-border text-muted-foreground hover:border-primary hover:text-primary'}`}>
                {r.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={onRemove}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
        </Button>
      </div>
    </div>
  );
};

const ReleaseEditor = ({ release, onSaved, onCancel }) => {
  const isNew = !release?.id;
  const [form, setForm] = useState({
    version: release?.version || '',
    title: release?.title || '',
    expires_at: release?.expires_at ? release.expires_at.split('T')[0] : '',
    features: release?.features?.length ? release.features : [{ ...BLANK_FEATURE }],
  });
  const [saving, setSaving] = useState(false);

  const updateFeature = (i, updated) => {
    const features = [...form.features];
    features[i] = updated;
    setForm(f => ({ ...f, features }));
  };
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  const addFeature = () => setForm(f => ({ ...f, features: [...f.features, { ...BLANK_FEATURE }] }));

  const save = async () => {
    if (!form.version.trim() || !form.title.trim()) { toast.error('Version label and title are required'); return; }
    if (form.features.some(f => !f.title.trim() || !f.description.trim())) { toast.error('All features need a title and description'); return; }
    setSaving(true);
    try {
      const res = isNew
        ? await api.post('/whats-new', { ...form, expires_at: form.expires_at ? `${form.expires_at}T23:59:59.000Z` : null })
        : await api.put(`/whats-new/${release.id}`, { ...form, expires_at: form.expires_at ? `${form.expires_at}T23:59:59.000Z` : null });
      toast.success(isNew ? 'Release created' : 'Release updated');
      onSaved(res.data);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label>Version Label *</Label>
          <Input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
            placeholder="e.g. May 2026" />
        </div>
        <div className="space-y-1">
          <Label>Modal Title *</Label>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. What's New in May 2026" />
        </div>
        <div className="space-y-1">
          <Label>Expires On <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input type="date" value={form.expires_at}
            onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} />
          <p className="text-[10px] text-muted-foreground">Banner hides automatically at end of this day</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Features</Label>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addFeature}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Feature
          </Button>
        </div>
        {form.features.map((feat, i) => (
          <FeatureEditor key={feat.id || i} feature={feat}
            onChange={updated => updateFeature(i, updated)}
            onRemove={() => removeFeature(i)} />
        ))}
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? 'Saving…' : isNew ? 'Create Release' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

const WhatsNewTab = () => {
  const isExpired = (r) => r.expires_at && new Date(r.expires_at) < new Date();

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const confirm = useConfirm();

  useEffect(() => { fetchReleases(); }, []);

  const fetchReleases = async () => {
    try {
      const res = await api.get('/whats-new');
      setReleases(res.data || []);
    } catch { toast.error('Failed to load releases'); }
    finally { setLoading(false); }
  };

  const handlePublish = async (id, isActive) => {
    try {
      const res = isActive
        ? await api.post(`/whats-new/${id}/unpublish`)
        : await api.post(`/whats-new/${id}/publish`);
      setReleases(prev => prev.map(r => r.id === id ? res.data : { ...r, is_active: false }));
      toast.success(isActive ? 'Release unpublished' : 'Release published — users will see it on next login');
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Delete this release? This cannot be undone.', destructive: true })) return;
    try {
      await api.delete(`/whats-new/${id}`);
      setReleases(prev => prev.filter(r => r.id !== id));
      toast.success('Release deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">What's New Releases</h2>
          <p className="text-sm text-muted-foreground">Create and publish feature announcements. Only one release can be active at a time. Users see the modal once per release.</p>
        </div>
        {!creating && (
          <Button onClick={async () => { setCreating(true); setEditingId(null); }} className="bg-primary hover:bg-primary/90 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Release
          </Button>
        )}
      </div>

      {creating && (
        <Card>
          <CardHeader><CardTitle className="text-base">New Release</CardTitle></CardHeader>
          <CardContent>
            <ReleaseEditor
              onSaved={(r) => { setReleases(prev => [r, ...prev]); setCreating(false); }}
              onCancel={() => setCreating(false)} />
          </CardContent>
        </Card>
      )}

      {releases.length === 0 && !creating ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No releases yet. Create your first What's New announcement.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {releases.map(r => (
            <Card key={r.id} className={r.is_active && !isExpired(r) ? 'border-primary border-2' : r.is_active && isExpired(r) ? 'border-orange-400 border-2' : ''}>
              <CardContent className="p-4">
                {editingId === r.id ? (
                  <ReleaseEditor release={r}
                    onSaved={(updated) => { setReleases(prev => prev.map(x => x.id === updated.id ? updated : x)); setEditingId(null); }}
                    onCancel={() => setEditingId(null)} />
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 cursor-pointer flex-1 min-w-0" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                        {expandedId === r.id ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{r.title}</span>
                            <Badge variant="outline" className="text-[10px]">{r.version}</Badge>
                            {r.is_active && !isExpired(r) && <Badge className="bg-primary text-white text-[10px]">Active</Badge>}
                            {r.is_active && isExpired(r) && <Badge className="bg-orange-100 text-orange-700 border border-orange-300 text-[10px]">Expired</Badge>}
                            {!r.is_active && <Badge variant="outline" className="text-[10px] text-muted-foreground">Draft</Badge>}
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{r.features?.length || 0} feature{r.features?.length !== 1 ? 's' : ''}{r.published_at ? ` · Published ${new Date(r.published_at).toLocaleDateString()}` : ''}{r.expires_at ? ` · Expires ${new Date(r.expires_at).toLocaleDateString()}` : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button variant="outline" size="sm" className="h-7 text-xs"
                          onClick={() => handlePublish(r.id, r.is_active)}>
                          {r.is_active ? <><CircleOff className="w-3.5 h-3.5 mr-1" /> {isExpired(r) ? 'Clear' : 'Unpublish'}</> : <><Radio className="w-3.5 h-3.5 mr-1" /> Publish</>}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={async () => { setEditingId(r.id); setExpandedId(null); }}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(r.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    {expandedId === r.id && (
                      <div className="mt-4 space-y-2 pl-7">
                        {(r.features || []).map((f, i) => (
                          <div key={f.id || i} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                            <IconComponent name={f.icon} className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{f.title}</p>
                              <p className="text-xs text-muted-foreground">{f.description}</p>
                              {(f.roles || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {f.roles.map(role => {
                                    const r = ROLES.find(x => x.value === role);
                                    return <span key={role} className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted border text-muted-foreground">{r?.label || role}</span>;
                                  })}
                                </div>
                              )}
                              {(f.roles || []).length === 0 && <span className="text-[10px] text-muted-foreground">All users</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default WhatsNewTab;
